import { forwardRef } from 'react';
import { cn } from '@/hen-ui/utils';
import { useIsClamped } from '@/hen-ui/hooks';
import { CheckboxButton } from '@/hen-ui/composites/CheckboxButton';
import { CollapsibleCard } from '@/hen-ui/composites/CollapsibleCard';
import { rankingCardVariants } from './ranking-card.variants';
import { RankingCardHeader } from './RankingCardHeader';
import { RankingCardBody } from './RankingCardBody';
import type { RankingCardProps } from './types';

export const RankingCard = forwardRef<HTMLDivElement, RankingCardProps>(
  (
    {
      variant = 'ranked',
      rank,
      name,
      nameHref,
      onNameClick,
      superlative,
      badges,
      exploreHref,
      onExploreClick,
      exploreLabel,
      children,
      stats,
      details,
      expanded = false,
      onExpandedChange,
      modalOpen = false,
      onModalOpenChange,
      showCompare = false,
      compared = false,
      onComparedChange,
      className,
      ...props
    },
    ref,
  ) => {
    const { ref: blurbRef, isClamped } = useIsClamped(!expanded);
    const hasDetails = (details?.length ?? 0) > 0;
    const showToggle =
      expanded || modalOpen || hasDetails || (children != null && isClamped);

    const headerNode = (
      <RankingCardHeader
        variant={variant}
        rank={rank}
        name={name}
        nameHref={nameHref}
        onNameClick={onNameClick}
        superlative={superlative}
        badges={badges}
        exploreHref={exploreHref}
        onExploreClick={onExploreClick}
        exploreLabel={exploreLabel}
      />
    );

    const compareCheckbox = showCompare ? (
      <CheckboxButton
        label='Compare'
        checked={compared}
        onCheckedChange={() => onComparedChange?.(!compared)}
      />
    ) : undefined;

    return (
      <CollapsibleCard
        ref={ref}
        className={cn(rankingCardVariants(), className)}
        ariaLabel={`${name} ranking card`}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        modalOpen={modalOpen}
        onModalOpenChange={onModalOpenChange}
        showToggle={showToggle}
        renderHeader={() => headerNode}
        renderBody={(isExpanded) => (
          <RankingCardBody
            stats={stats}
            details={details}
            expanded={isExpanded}
            blurbRef={blurbRef}
          >
            {children}
          </RankingCardBody>
        )}
        footerLeft={compareCheckbox}
        renderDrawerHeader={() => headerNode}
        renderDrawerBody={() => (
          <RankingCardBody stats={stats} details={details} expanded>
            {children}
          </RankingCardBody>
        )}
        drawerHeaderClassName='px-lg pt-md pb-0'
        drawerFooterLeft={compareCheckbox}
        {...props}
      />
    );
  },
);

RankingCard.displayName = 'RankingCard';
